import React, { useState } from 'react';
import Field from '../Field';
import { SaveState } from '../SaveDot';
import SearchableContactDropdown from '../SearchableContactDropdown';
import ContactInfoButton from '../ContactInfoButton';
import NewContactCard from '../NewContactCard';
import { stageContactInheritLabel } from '../ProjectDetailModal/config';

// =======================================
// INTERFACES
// =======================================

interface StageContactFieldProps {
  /** The stage's own contact id; '' inherits the project's. */
  value: string;
  /**
   * The chosen contact's id ('' = inherit) and display name. The name comes
   * with it so a caller showing the contact elsewhere need not look it up.
   */
  onChange: (contactId: string, fullName: string | null) => void;
  /** The project's client: a stage's contact is one of its contacts. */
  clientId: number | string | null;
  /** The project's own primary contact, named in the "inherit" entry. */
  projectContactName: string | null;
  /**
   * False while there is no project to inherit from yet (the Add Stage modal
   * before a parent is picked): the entry then reads plain "Inherit from
   * project" rather than claiming the project has no contact.
   */
  hasProject?: boolean;
  hint?: string;
  className?: string;
  saveState?: SaveState;
}

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * A stage's primary contact: one of the project's client's contacts, or
 * nothing chosen, which inherits the project's (see DEVELOPMENT.md "A stage's
 * primary contact: NULL inherits"). The one control every place that sets it
 * uses -- the Add Stage modal, the Stages tab form and the stage's own modal --
 * so the inherit wording, the client filter and "+ Add contact" stay in step.
 */
const StageContactField: React.FC<StageContactFieldProps> = ({
  value,
  onChange,
  clientId,
  projectContactName,
  hasProject = true,
  hint = "Leave as inherited to use the project's contact.",
  className,
  saveState,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  // Remounts the dropdown so it lists a contact just created.
  const [refreshKey, setRefreshKey] = useState(0);
  const clientNumber = clientId != null && clientId !== '' ? Number(clientId) : null;

  return (
    <Field
      label="Primary contact"
      className={className}
      saveState={saveState}
      hint={hint}
      action={clientNumber != null ? (
        <button
          type="button"
          className="panel-heading__action"
          onClick={() => setIsAdding((open) => !open)}
        >
          {isAdding ? 'Cancel' : '+ Add contact'}
        </button>
      ) : undefined}
    >
      <div className="contact-field">
        <SearchableContactDropdown
          // A new client means a new contact list.
          key={`${clientNumber ?? 'none'}-${refreshKey}`}
          value={value || null}
          onChange={(contactId, contact) => onChange(
            contactId != null ? String(contactId) : '',
            contact ? `${contact.first_name} ${contact.last_name}`.trim() : null
          )}
          allowNone
          noneLabel={hasProject ? stageContactInheritLabel(projectContactName) : 'Inherit from project'}
          clientId={clientNumber ?? undefined}
          disabled={clientNumber == null}
        />
        <ContactInfoButton contactId={value || null} />
      </div>
      {isAdding && clientNumber != null && (
        <NewContactCard
          clientId={clientNumber}
          onCancel={() => setIsAdding(false)}
          onCreated={(contactId, fullName) => {
            onChange(contactId, fullName);
            setRefreshKey((k) => k + 1);
            setIsAdding(false);
          }}
        />
      )}
    </Field>
  );
};

export default StageContactField;
